import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuBuilding, LuPlus, LuPencil, LuTrash2, LuEye, LuX, LuCheck, LuSearch, LuBuilding2,
} from 'react-icons/lu';

const TYPE_LABEL = { matriz: 'Matriz', filial: 'Filial' };
const TYPE_COLOR = {
  matriz: { bg: 'var(--info-soft)', color: 'var(--info)' },
  filial: { bg: 'var(--warning-soft)', color: 'var(--warning)' },
};

const UF_LIST = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA',
  'MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN',
  'RS','RO','RR','SC','SP','SE','TO',
];

const emptyForm = {
  name: '', trade_name: '', cnpj: '', type: 'matriz', parent_company_id: '',
  phone: '', email: '', address: '', city: '', state: '', notes: '', active: true,
};

function maskCNPJ(v) {
  return v.replace(/\D/g, '').slice(0, 14)
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function maskPhone(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').trim();
  return d.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').trim();
}

// ─── Modal de cadastro / edição ───────────────────────────────────────────────

function CompanyModal({ editing, onClose, onSaved }) {
  const [form,      setForm]      = useState(editing ? { ...emptyForm, ...editing } : { ...emptyForm });
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [matrizList, setMatrizList] = useState([]);

  // Lista de matrizes disponíveis para o campo "Empresa Matriz" — busca TODAS
  // as empresas (GET /companies, ignorando a empresa ativa do CompanySwitcher
  // com o header vazio — senão, com uma filial selecionada como ativa, essa
  // lista viria restrita a 1 empresa) e filtra type === 'matriz' no cliente.
  useEffect(() => {
    api.get('/companies', { headers: { 'X-Active-Company-Id': '' } })
      .then(({ data }) => setMatrizList((data || []).filter((c) => c.type === 'matriz' && c.id !== editing?.id)))
      .catch(() => setMatrizList([]));
  }, [editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Razão social é obrigatória'); return; }
    if (form.type === 'filial' && !form.parent_company_id) {
      setError('Selecione a empresa matriz desta filial');
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        ...form,
        // Matriz nunca tem empresa matriz própria (só 2 níveis) — o backend
        // também força isso, mas evita mandar lixo do form.
        parent_company_id: form.type === 'filial' ? Number(form.parent_company_id) : null,
      };
      const { data } = editing
        ? await api.put(`/companies/${editing.id}`, payload)
        : await api.post('/companies', payload);
      onSaved(data, !!editing);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.40)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'var(--surface-1)', borderRadius:12, width:'100%', maxWidth:560,
        boxShadow:'0 12px 40px rgba(0,0,0,0.20)', maxHeight:'90vh', overflowY:'auto' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'20px 24px 0', position:'sticky', top:0, background:'var(--surface-1)', zIndex:1 }}>
          <div style={{ fontWeight:700, fontSize:15, color:'var(--text-1)', display:'flex', alignItems:'center', gap:8 }}>
            <LuBuilding2 color="var(--accent)" />
            {editing ? 'Editar Empresa' : 'Nova Empresa'}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-3)' }}>
            <LuX size={16} />
          </button>
        </div>

        <div style={{ padding:'16px 24px 24px', display:'flex', flexDirection:'column', gap:12 }}>
          {error && <div className="alert-custom danger" style={{ fontSize:13 }}>{error}</div>}

          {/* Linha 1: Razão Social + Tipo */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:10 }}>
            <div>
              <label className="form-label">Razão Social *</label>
              <input className="form-control" value={form.name} autoFocus
                onChange={(e) => set('name', e.target.value)} placeholder="Nome jurídico da empresa" />
            </div>
            <div>
              <label className="form-label">Tipo</label>
              <select className="form-control" value={form.type} onChange={(e) => set('type', e.target.value)}>
                <option value="matriz">Matriz</option>
                <option value="filial">Filial</option>
              </select>
            </div>
          </div>

          {/* Empresa Matriz — só quando type === 'filial' */}
          {form.type === 'filial' && (
            <div>
              <label className="form-label">Empresa Matriz *</label>
              <select className="form-control" value={form.parent_company_id || ''}
                onChange={(e) => set('parent_company_id', e.target.value)}>
                <option value="">Selecione a matriz…</option>
                {matrizList.map((m) => (
                  <option key={m.id} value={m.id}>{m.trade_name || m.name}</option>
                ))}
              </select>
              {matrizList.length === 0 && (
                <div style={{ fontSize:12, color:'var(--danger)', marginTop:4 }}>
                  Nenhuma empresa matriz cadastrada ainda.
                </div>
              )}
            </div>
          )}

          {/* Nome Fantasia */}
          <div>
            <label className="form-label">Nome Fantasia</label>
            <input className="form-control" value={form.trade_name}
              onChange={(e) => set('trade_name', e.target.value)} placeholder="Como é conhecida comercialmente" />
          </div>

          {/* Linha 2: CNPJ + Telefone */}
          <div className="resp-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label className="form-label">CNPJ</label>
              <input className="form-control" value={form.cnpj}
                onChange={(e) => set('cnpj', maskCNPJ(e.target.value))}
                placeholder="00.000.000/0000-00" maxLength={18} />
            </div>
            <div>
              <label className="form-label">Telefone</label>
              <input className="form-control" value={form.phone}
                onChange={(e) => set('phone', maskPhone(e.target.value))}
                placeholder="(00) 00000-0000" maxLength={15} />
            </div>
          </div>

          {/* E-mail */}
          <div>
            <label className="form-label">E-mail</label>
            <input className="form-control" type="email" value={form.email}
              onChange={(e) => set('email', e.target.value)} placeholder="contato@empresa.com.br" />
          </div>

          {/* Endereço */}
          <div>
            <label className="form-label">Endereço</label>
            <input className="form-control" value={form.address}
              onChange={(e) => set('address', e.target.value)} placeholder="Rua, número, complemento" />
          </div>

          {/* Linha 3: Cidade + UF */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:10 }}>
            <div>
              <label className="form-label">Cidade</label>
              <input className="form-control" value={form.city}
                onChange={(e) => set('city', e.target.value)} placeholder="São Paulo" />
            </div>
            <div>
              <label className="form-label">UF</label>
              <select className="form-control" value={form.state} onChange={(e) => set('state', e.target.value)}
                style={{ minWidth:70 }}>
                <option value="">—</option>
                {UF_LIST.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
              </select>
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="form-label">Observações</label>
            <textarea className="form-control" rows={3} value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Informações adicionais…"
              style={{ resize:'vertical', fontFamily:'inherit', fontSize:13 }} />
          </div>

          {/* Ativo */}
          <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, cursor:'pointer' }}>
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
            Empresa ativa
          </label>

          {/* Ações */}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
            <button onClick={onClose}
              style={{ padding:'9px 20px', border:'1px solid var(--border)', borderRadius:8,
                background:'var(--surface-1)', cursor:'pointer', fontSize:13, fontFamily:'inherit', color:'var(--text-2)' }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding:'9px 22px', border:'none', borderRadius:8, cursor:'pointer',
                fontSize:13, fontWeight:700, fontFamily:'inherit', background:'var(--accent)', color:'var(--on-accent)',
                display:'flex', alignItems:'center', gap:6, opacity: saving ? 0.7 : 1 }}>
              <LuCheck /> {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modal de visualização ────────────────────────────────────────────────────

function ViewModal({ company, onClose, onEdit }) {
  const tc = TYPE_COLOR[company.type] || TYPE_COLOR.matriz;
  const Row = ({ label, value }) => value ? (
    <div style={{ display:'flex', gap:8, fontSize:13, padding:'6px 0', borderBottom:'1px solid var(--border)' }}>
      <span style={{ color:'var(--text-3)', minWidth:130, flexShrink:0 }}>{label}</span>
      <span style={{ color:'var(--text-1)', fontWeight:500 }}>{value}</span>
    </div>
  ) : null;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.40)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'var(--surface-1)', borderRadius:12, width:'100%', maxWidth:500,
        boxShadow:'0 12px 40px rgba(0,0,0,0.20)', maxHeight:'90vh', overflowY:'auto' }}>

        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 24px 0' }}>
          <div style={{ fontWeight:700, fontSize:15, color:'var(--text-1)', display:'flex', alignItems:'center', gap:8 }}>
            <LuBuilding2 color="var(--accent)" />
            {company.trade_name || company.name}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-3)' }}>
            <LuX size={16} />
          </button>
        </div>

        <div style={{ padding:'16px 24px 8px' }}>
          <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700,
            background: tc.bg, color: tc.color }}>
            {TYPE_LABEL[company.type]}
          </span>
          {!company.active && (
            <span style={{ marginLeft:6, padding:'2px 10px', borderRadius:20, fontSize:11,
              fontWeight:700, background:'var(--surface-2)', color:'var(--text-3)' }}>Inativa</span>
          )}
        </div>

        <div style={{ padding:'0 24px 8px' }}>
          <Row label="Razão Social"    value={company.name} />
          <Row label="Nome Fantasia"   value={company.trade_name} />
          <Row label="CNPJ"            value={company.cnpj} />
          <Row label="Telefone"        value={company.phone} />
          <Row label="E-mail"          value={company.email} />
          <Row label="Endereço"        value={company.address} />
          <Row label="Cidade/UF"       value={[company.city, company.state].filter(Boolean).join(' — ')} />
          <Row label="Observações"     value={company.notes} />
        </div>

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', padding:'12px 24px 20px' }}>
          <button onClick={onClose}
            style={{ padding:'8px 18px', border:'1px solid var(--border)', borderRadius:8,
              background:'var(--surface-1)', cursor:'pointer', fontSize:13, fontFamily:'inherit', color:'var(--text-2)' }}>
            Fechar
          </button>
          <button onClick={() => { onClose(); onEdit(company); }}
            style={{ padding:'8px 18px', border:'none', borderRadius:8, cursor:'pointer',
              fontSize:13, fontWeight:700, fontFamily:'inherit', background:'var(--surface-3)', color:'var(--text-1)',
              display:'flex', alignItems:'center', gap:6 }}>
            <LuPencil size={12} /> Editar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [search,    setSearch]    = useState('');
  const [loading,   setLoading]   = useState(false);
  const [modal,     setModal]     = useState(null);   // null | 'new' | {company} (edit) | {view: company}
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');

  const load = async (q = search) => {
    setLoading(true);
    try {
      const { data } = await api.get('/companies', { params: q ? { search: q } : {} });
      setCompanies(data);
    } catch { setError('Erro ao carregar empresas'); }
    finally   { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSaved = (saved, isEdit) => {
    setCompanies((prev) =>
      isEdit
        ? prev.map((c) => (c.id === saved.id ? saved : c))
        : [...prev, saved].sort((a, b) => a.name.localeCompare(b.name))
    );
    setModal(null);
    setSuccess(isEdit ? 'Empresa atualizada com sucesso' : 'Empresa cadastrada com sucesso');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Remover "${company.trade_name || company.name}"?\n\nEsta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/companies/${company.id}`);
      setCompanies((prev) => prev.filter((c) => c.id !== company.id));
      setSuccess('Empresa removida');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao remover empresa');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  const filtered = companies; // filtro já aplicado na API

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Empresas / Filiais</div>
          <div className="page-card-subtitle">Cadastro de empresas contratantes e filiais</div>
        </div>
        <button className="btn-salvar" onClick={() => setModal('new')}>
          <LuPlus style={{ marginRight:6 }} /> Nova Empresa
        </button>
      </div>

      {error   && <div className="alert-custom danger"  style={{ marginBottom:12 }}>{error}</div>}
      {success && <div className="alert-custom success" style={{ marginBottom:12 }}>{success}</div>}

      {/* Busca */}
      <form onSubmit={handleSearch}
        style={{ display:'flex', gap:8, marginBottom:16, maxWidth:420 }}>
        <input className="form-control" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome, fantasia ou CNPJ…" />
        <button type="submit"
          style={{ padding:'8px 16px', border:'none', borderRadius:8, cursor:'pointer',
            background:'var(--surface-3)', color:'var(--text-1)', display:'flex', alignItems:'center', gap:6,
            fontSize:13, fontFamily:'inherit', fontWeight:600, whiteSpace:'nowrap' }}>
          <LuSearch size={13} /> Buscar
        </button>
      </form>

      {loading && <div style={{ color:'var(--text-3)', fontSize:13, padding:'16px 0' }}>Carregando…</div>}

      {!loading && filtered.length === 0 && (
        <div className="empty-state">
          <LuBuilding size={32} color="var(--text-3)" style={{ marginBottom:8 }} />
          <p>Nenhuma empresa cadastrada</p>
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:'2px solid var(--border)' }}>
                {['Razão Social / Fantasia', 'CNPJ', 'Tipo', 'Cidade/UF', 'Telefone', 'Status', ''].map((h) => (
                  <th key={h} style={{ padding:'9px 12px', textAlign:'left', color:'var(--text-3)', fontWeight:600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const tc = TYPE_COLOR[c.type] || TYPE_COLOR.matriz;
                return (
                  <tr key={c.id} style={{ borderBottom:'1px solid var(--border)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'10px 12px' }}>
                      <div style={{ fontWeight:700, color:'var(--text-1)' }}>{c.name}</div>
                      {c.trade_name && <div style={{ fontSize:12, color:'var(--text-3)', marginTop:1 }}>{c.trade_name}</div>}
                    </td>
                    <td style={{ padding:'10px 12px', color:'var(--text-2)', fontFamily:'monospace', fontSize:12 }}>
                      {c.cnpj || '—'}
                    </td>
                    <td style={{ padding:'10px 12px' }}>
                      <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700,
                        background: tc.bg, color: tc.color }}>
                        {TYPE_LABEL[c.type]}
                      </span>
                    </td>
                    <td style={{ padding:'10px 12px', color:'var(--text-2)' }}>
                      {[c.city, c.state].filter(Boolean).join(' — ') || '—'}
                    </td>
                    <td style={{ padding:'10px 12px', color:'var(--text-2)' }}>{c.phone || '—'}</td>
                    <td style={{ padding:'10px 12px' }}>
                      <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700,
                        background: c.active ? 'var(--success-soft)' : 'var(--surface-2)',
                        color:      c.active ? 'var(--success)' : 'var(--text-3)' }}>
                        {c.active ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td style={{ padding:'10px 12px', textAlign:'right', whiteSpace:'nowrap' }}>
                      <button onClick={() => setModal({ view: c })} title="Visualizar" style={iconBtn('var(--info)')}>
                        <LuEye size={13} />
                      </button>
                      <button onClick={() => setModal(c)} title="Editar" style={iconBtn('var(--text-1)')}>
                        <LuPencil size={13} />
                      </button>
                      <button onClick={() => handleDelete(c)} title="Remover" style={iconBtn('var(--danger)')}>
                        <LuTrash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize:12, color:'var(--text-3)', padding:'10px 4px' }}>
            {filtered.length} empresa{filtered.length !== 1 ? 's' : ''} cadastrada{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      {modal === 'new' && (
        <CompanyModal editing={null} onClose={() => setModal(null)} onSaved={handleSaved} />
      )}
      {modal && modal !== 'new' && !modal.view && (
        <CompanyModal editing={modal} onClose={() => setModal(null)} onSaved={handleSaved} />
      )}
      {modal?.view && (
        <ViewModal
          company={modal.view}
          onClose={() => setModal(null)}
          onEdit={(c) => setModal(c)}
        />
      )}
    </div>
  );
}

const iconBtn = (color) => ({
  background:'none', border:'none', cursor:'pointer',
  color, padding:'4px 6px', borderRadius:5,
});
